import {AzureClickFeatureHandler} from "@esWebAPI/es/map/handler/AzureClickFeatureHandler";

import type {AzureBaseMap} from "./AzureBaseMap";
import type {TerritoryLayerCollection} from "@esWebAPI/es/map/layer/territory/TerritoryLayerCollection";
import type Layer from "./Layer";
import type { IEventChannel } from "@esWebAPI/es/model/Models";
import {AzureOverlay} from "@esWebAPI/es/map/module/AzureOverlay";

interface SelectionItem {
    layerViewId: number,
    featureId: number,
    editing: boolean
}

export class AzureSelect {

    private readonly layer: Layer
    private readonly eventChannel: IEventChannel;

    private readonly clickFeatureHandler: AzureClickFeatureHandler;

    private selection: SelectionItem | null = null;

    private selectionEnabled: boolean = true;

    private baseMap: AzureBaseMap;

    private readonly overlayModule: AzureOverlay;

    constructor(baseMap: AzureBaseMap, layer: Layer, territoryLayerCollection: TerritoryLayerCollection | null, overlayModule: AzureOverlay, eventChannel: IEventChannel) {
        this.baseMap = baseMap;
        this.layer = layer;
        this.overlayModule = overlayModule;
        this.eventChannel = eventChannel;

        this.eventChannel.addListener("onCommitFeatureEdits", rs => {
            if (this.selection != null) {
                this.selection.editing = false;
            }
        });
        this.clickFeatureHandler = new AzureClickFeatureHandler(baseMap, territoryLayerCollection);
        this.clickFeatureHandler.eventChannel.addListener("onFindFeature", (feature: any, location: [number, number]) => {
            if(!this.selectionEnabled || this.isInEditMode(feature.layerViewId, feature.id)) {
                return;
            }

            es.util.TrackingEvents.push(['VM.Map.Select_Feature']);
            this.select(feature.layerViewId, feature.id, location);

        })
        this.clickFeatureHandler.eventChannel.addListener("onFindFeatures", (layers: any, location: [number, number]) => {
            if (this.selectionEnabled) {
                if (this.selection?.editing) {
                    const message = $i18n("Select_feature_editing");
                    es.alert.confirm({
                        message,
                        labels: {
                            ok: $i18n("CONTINUE_EDITING"),
                            cancel: $i18n("DISCARD_CHANGES")
                        },
                        allowClose: true,
                        onok: () => {
                            console.log("Stay    editing")
                        },
                        oncancel: () => {
                            esApp.map.edit.revert();
                            this.deselectFeature(this.selection.layerViewId, this.selection.featureId);
                            // this.clear();
                            // es.util.TrackingEvents.push(['VM.Map.Select_Feature']);
                            eventChannel.fireEvent("onSelectFeatureFoundMultipleFeatures", layers, location, this.selection);
                            console.log("Cancel    editing")
                        }
                    })
                } else {
                    eventChannel.fireEvent("onSelectFeatureFoundMultipleFeatures", layers, location, this.selection);
                }
            }
        });
        this.clickFeatureHandler.eventChannel.addListener("onOverlayFeatureClick", (feat: any, location: [number, number]) => {
            if (this.selectionEnabled) {
                eventChannel.fireEvent("onOverlayFeatureClick", feat, location);
            }
        });
    }

    private selectFeature(feature: any, location?: [number, number], extraSelectFeatureEventInfo?: any) {

        this.selection = {
            editing: false,
            featureId: feature.id,
            layerViewId: feature.layerViewId
        };

        if(!location && feature?.geom){
            const geom = feature.geom;
            if (geom[0] === "PT") {         //If feature is a point, the select location should be the point
                location = geom.slice(1);
            }
            else {
                let bounds;
                let area = 0;
                if (geom[0] === "MP"){
                    bounds = es.util.bounds.valueOf(geom[1][0]);
                    for(let i = geom.length; i-- > 1;){
                        const ringBounds = es.util.bounds.valueOf(geom[i][0]);
                        const ringArea = (ringBounds[2]-ringBounds[0])*(ringBounds[3]*ringBounds[1]);
                        if((!bounds) || (ringArea > area)){
                            bounds = ringBounds;
                            area = ringArea;
                        }
                    }
                }
                else{
                    bounds = es.api.geom.getBounds(geom);
                }
                location = [(bounds[0] + bounds[2]) / 2, (bounds[1] + bounds[3]) / 2];
            }
        }

        if (location || feature.id == 999999999999999) {
            if (es.isDeveloper){
                //no need to fire SelectFeature for NEW or WITH ERROR features.
                //TOR9-10787 - 999999999999999 is special id for no geom outside aggregate row... we need to throw event there as well
                //console.log("selectFeature", feature?.label, feature);
            }
            this.eventChannel.fireEvent("onSelectFeature", feature, location, extraSelectFeatureEventInfo);
        }
    }

    private isSelected(layerViewId: number, featureId: number): boolean {
        // es.log.log("Checking if isSelected: layerViewID = ", layerViewId, " featureId = ", featureId);
        return (this.selection?.featureId === featureId && this.selection?.layerViewId === layerViewId);
    }

    /**
     * Select the feature matching the layerViewId and featureId
     * @param layerViewId Id of LayerView
     * @param featureId Id of Feature
     * @param location Lon/Lat coordinates?
     * @param opts Options for cache/req broker
     */
    public select(layerViewId: number, featureId: number, location?: [number, number], opts?: any): any {
        es.log.log({layerViewId, featureId, location, zoom: this.baseMap.getZoom()});
        let attrs = null;
        let dynamicAttrs = null;
        const lvm = this.layer.getById(layerViewId);
        if (lvm.isAttributeNamesOverwritten()) {
            attrs = lvm.getAttributeNames();
            dynamicAttrs = lvm.getDynamicAttributes();
        }

        return es.api.feature.get(layerViewId, featureId, attrs, dynamicAttrs).then((feature: any) => {
            if (!this.selectionEnabled || !feature || this.isSelected(feature.layerViewId, feature.id)) {
                return;
            }

            if (!this.selection?.editing) {
                if (this.selection) {
                    this.deselectFeature(this.selection.layerViewId, this.selection.featureId);
                }
                this.selectFeature(feature, location, opts?.extraSelectFeatureEventInfo);
                return feature;
            }

            const dfd = $.Deferred();
            const message = $i18n("Select_feature_editing");
            es.alert.confirm({
                message,
                labels: {
                    ok: $i18n("CONTINUE_EDITING"),
                    cancel: $i18n("DISCARD_CHANGES")
                },
                allowClose: true,
                onok: () => {
                    console.log("Stay editing")
                    dfd.reject();
                },
                oncancel: () => {
                    esApp.map.edit.revert();
                    this.deselectFeature(this.selection.layerViewId, this.selection.featureId)
                    // this.clear();
                    es.util.TrackingEvents.push(['VM.Map.Select_Feature']);
                    this.selectFeature(feature, location, opts?.extraSelectFeatureEventInfo);
                    dfd.resolve(feature);
                    console.log("Cancel editing")
                }
            })
            return dfd.promise();
        });
    }

    public deselect(layerViewId: number, featureId: number): boolean {
        // es.log.log("AzureSelect.deselect", layerViewId, featureId);
        if (this.selection?.editing) {
            // throw new Error("Select is in Edit mode");
            es.log.warn("We are deselecting a feature while EDITING. isSelected=", this.isSelected(layerViewId, featureId));
        }

        return this.deselectFeature(layerViewId, featureId);
    }

    private deselectFeature(layerViewId: number, featureId: number): boolean {
        if (this.isSelected(layerViewId, featureId)) {
            let selected = this.selection;
            this.selection = null;
            this.eventChannel.fireEvent("onDeselectFeature", selected);
            return true;
        }
        return false;
    }

    public getSelected(): SelectionItem | null {
        return this.selection;
    }

    public setEditMode() {
        if (!this.selection) {
            es.log.error("Error, there is nothing selected");
            return;
        }
        this.selection.editing = true;
    }

    public isInEditMode(layerViewId: number, featureId: number) {
        if (this.isSelected(layerViewId, featureId)) {
            return this.selection.editing;
        }
        return false;
    }

    public confirmRevertEdit(){
        const dfd = $.Deferred();
        if (this.selection && this.isInEditMode(this.selection.layerViewId, this.selection.featureId)) {
            const message = $i18n("Select_feature_editing");
            es.alert.confirm({
                message,
                labels: {
                    ok: $i18n("CONTINUE_EDITING"),
                    cancel: $i18n("DISCARD_CHANGES")
                },
                allowClose: true,
                onok: () => {
                    dfd.reject();
                },
                oncancel: () => {
                    esApp.map.edit.revert();
                    this.deselectFeature(this.selection.layerViewId, this.selection.featureId)
                    dfd.resolve();
                }
            })
        } else {
            dfd.resolve();
        }
        return dfd.promise();
    }
    public setSelectionEnabled(enabled: boolean) {
        this.selectionEnabled = enabled;
        this.clickFeatureHandler.selectionEnabled = enabled;
        this.eventChannel.fireEvent("onSetSelectionEnabled", enabled);
    }

    public isSelectionEnabled() {
        return this.selectionEnabled;
    }

    public clear(){
        if (this.selection) {
            if (this.selection?.editing) {
                // throw new Error("Select is in Edit mode");
                es.log.warn("Trying to clear while EDITING");
            }

            let selected = this.selection;
            this.selection = null;
            this.eventChannel.fireEvent("onDeselectFeature", selected);
        }
        this.eventChannel.fireEvent("onClearSelections");
    }

    /**
     * testim helper method
     * Simulate a mouseclick on the map canvas. Hopefully will be more reliable than relying on recorded mouseclicks.
     * e.g.:
     *     esApp.map.select.simulateClickAtCoordinates([-6.227245, 53.356821])
     * @param lonLat
     * @param zoom optional, defaults to current zoom level
     */
    public async simulateClickAtCoordinates(lonLat: [number, number], zoom: number | undefined): Promise<void> {

        if(!zoom){
            zoom = this.baseMap.getZoom();
        }

        await this.baseMap.setCenterAndZoom(lonLat[0], lonLat[1], zoom);

        // wait for map to re-render (hopefully long enough)
        await new Promise(resolve => setTimeout(resolve, 1000));

        this.overlayModule.pulsePoint(lonLat, true, 5000); // enable to see mouseclick point on map

        const canvas = this.baseMap.map.getCanvasContainer().querySelector('canvas');
        const canvasRect = canvas.getBoundingClientRect();

        const centerX = canvasRect.left + canvasRect.width / 2;
        const centerY = canvasRect.top + canvasRect.height / 2;

        es.log.log("simulateClickAtCoordinates", lonLat, [centerX, centerY]);

        canvas.dispatchEvent(new MouseEvent('click', {
            view: window,
            bubbles: true,
            cancelable: true,
            clientX: centerX,
            clientY: centerY,
            button: 0
        }));
    }
}
